import VectorSource from 'ol/source/Vector';
import VectorImageLayer from 'ol/layer/VectorImage';
import EsriJSON from 'ol/format/EsriJSON';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import { Fill, Stroke, Style } from 'ol/style';
import Text from 'ol/style/Text';
import type { FeatureLike } from 'ol/Feature';

interface SitgLayerOptions {
  visible: boolean;
  layerName: string;
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  zIndex?: number;
  labelField?: string;
  labelMinZoom?: number;
}

export function createSitgLayer(options: SitgLayerOptions) {
  const {
    visible,
    layerName,
    fillColor = 'rgba(255, 0, 0, 0.1)',
    strokeColor = '#FF0000',
    strokeWidth = 1,
    zIndex = 10,
    labelField,
    labelMinZoom = 17,
  } = options;

  const polygonStyle = new Style({
    fill: new Fill({ color: fillColor }),
    stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
  });

  const labelCollisionParcelles = new Set<string>();
  const labelCollisionOffset: [number, number] = [12, -12];

  const style = labelField
    ? (feature: FeatureLike, resolution: number) => {
        const maxResolution = 156543.03392804097 / Math.pow(2, labelMinZoom);
        if (resolution >= maxResolution) {
          return polygonStyle;
        }

        const label = feature.get(labelField);
        if (label == null || label === '') {
          return polygonStyle;
        }

        const hasDotCollision = labelCollisionParcelles.has(String(label));

        return new Style({
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
          text: new Text({
            text: String(label),
            font: 'bold 11px sans-serif',
            fill: new Fill({ color: '#333333' }),
            stroke: new Stroke({ color: '#ffffff', width: 3 }),
            overflow: true,
            offsetX: hasDotCollision ? labelCollisionOffset[0] : 0,
            offsetY: hasDotCollision ? labelCollisionOffset[1] : 0,
          }),
        });
      }
    : polygonStyle;

  const layer = new VectorImageLayer({
    source: new VectorSource({
      format: new EsriJSON(),
      url: (extent) => {
        const bbox = extent.join(',');
        return `https://vector.sitg.ge.ch/arcgis/rest/services/Hosted/${layerName}/FeatureServer/0/query?where=1%3D1&outFields=*&returnGeometry=true&geometry=${bbox}&geometryType=esriGeometryEnvelope&inSR=3857&outSR=3857&spatialRel=esriSpatialRelIntersects&f=json`;
      },
      strategy: bboxStrategy,
      wrapX: true,
    }),
    visible,
    properties: { name: `sitg_${layerName.toLowerCase()}` },
    style,
    zIndex,
  });

  (layer as any).setLabelCollisionParcelles = (parcelleNos: string[]) => {
    labelCollisionParcelles.clear();
    parcelleNos.forEach((no) => labelCollisionParcelles.add(String(no)));
    layer.changed();
  };

  return layer;
} 